import React, { useState, useEffect, useMemo } from "react";
import { Box, Chip, Paper, TextField, Typography } from "@mui/material";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Button from "@/components/Button";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import { ParticipantResponse } from "@/domain/participant.interface";
import GenericAlertModal from "@/components/AlertModal";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import SkeletonLoader from "@/components/SkeletonLoader";
import { useCategoryByType } from "@/hooks/useCategories";
import { CategoryElement } from "@/domain/category.interface";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { TFunction } from "i18next";
import { InferType } from "yup";
import Select from "@/components/Select";
import { useAddManualReimbursement } from "@/hooks/useEnrollments";
import { ErrorResponse } from "@/domain/error.interface";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { usePermissionAlert } from "@/hooks/usePermissionAlert";

interface ManualReimbursementModalProps {
  open: boolean;
  onClose: () => void;
  participantResponse: ParticipantResponse;
  refetchParticipant: () => void;
}

const ManualReimbursementModal: React.FC<ManualReimbursementModalProps> = ({
  open,
  onClose,
  participantResponse,
  refetchParticipant,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { lastEnrollment: enrollment, participant } = participantResponse;
  const { userInfo, userHasAccessToCode } = useUserStore();
  const { showPermissionAlert, PermissionAlertModal } = usePermissionAlert("clients");

  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const createAuditLog = useCreateAuditlog();

  const addManualReimbursement = useAddManualReimbursement(enrollment?._id);
  const { category, isLoadingCategory } = useCategoryByType({
    type: "REIMBURSEMENT_REASON",
    status: "ACTIVE",
  });

  const reasons = useMemo(() => {
    if (!category) return [];

    return category?.categories.map((categoryElement: CategoryElement) => {
      const name = categoryElement.languages.find(
        (language) => language.language?.toLocaleLowerCase() === "en-us"
      )?.name as string;

      return {
        key: categoryElement._id,
        value: name,
      };
    });
  }, [category]);

  const {
    handleSubmit,
    control,
    formState: { errors },
    trigger,
  } = useForm<ManualReimbursementFormData>({
    resolver: yupResolver(addSchema(t)),
    mode: "all",
    defaultValues: {
      reason: "",
      amount: undefined,
      comments: "",
    },
  });

  const handleClose = () => {
    clearState();
    onClose();
    refetchParticipant();
  };

  const clearState = () => {
    setIsConfirmationOpen(false);
    setIsLoading(false);
  };

  const handleReimbursement = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    if (!userHasAccessToCode("PARTICIPANT_REWARD_MANUAL_SUBMIT")) {
      showPermissionAlert();
      return;
    }
    setIsConfirmationOpen(true);

  };


  const participantFullName = useMemo(() => {
    return `${participant.firstName} ${participant.lastName}`;
  }, [participant]);

  const userFullName = useMemo(() => {
    return `${userInfo?.firstName} ${userInfo?.lastName}`;
  }, [userInfo]);

  const onSubmit = async (data: ManualReimbursementFormData) => {
    const payload = {
      reason: data.reason as string,
      amount: Math.round(Number(data.amount) * 100) / 100,
      comments: data.comments as string,
    };

    try {
      setIsConfirmationOpen(false);
      await addManualReimbursement.mutateAsync(payload, {
        onSuccess: () => {
          createAuditLog.mutate({
            appType: "WEB_BACK_OFFICE",
            module: MODULE_NAME.PARTICIPANT_REWARDS,
            option: OPTIONS.PARTICIPANT_MANUAL_REIMBURSEMENT,
            actionCode: ACTION_CODE.WEB_PART_REWARDS_MANUAL_REWARD,
            action: "When a manual reimbursement is submitted",
            detail: `A manual reimbursement was provided by ${userFullName} to ${participantFullName}`,
            transactionDate: new Date(),
            accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
            createdBy: userInfo?._id as string,
            programId: participantResponse?.lastEnrollment.programId as string,
            targetId: participantResponse?.participant._id,
            orgUnitId: participantResponse?.lastEnrollment.orgUnitId as string,
          });
          onClose();
          refetchParticipant();
          showSnackBar(
            "The reimbursement has been added to the client's balance.",
            "success"
          );
        },
        onError: (error: ErrorResponse) => {
          showSnackBar(
            (Array.isArray(error.message) ? error.message[0] : error.message) ||
            error.error ||
            "An error occurred"
          );
        },
      });
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
        err.error ||
        "An error occurred"
      );
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        isPending={isLoading}
        title={"Manual Reimbursement"}
        theme={theme}
        size={{
          width: "100%",
          minHeight: 440,
          maxHeight: 600,
          maxWidth: 640,
        }}
        content={
          <Box mt={4} sx={{ width: "100%" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={12}>
                <Typography
                  variant="titleSmall"
                  fontWeight={700}
                  gutterBottom
                  color={theme.palette.menu.dark}
                >
                  Client:
                  <Typography
                    variant="titleSmall"
                    fontWeight={400}
                    gutterBottom
                    color={theme.palette.icon.dark}
                  >
                    {" "}
                    {participantFullName}
                  </Typography>
                </Typography>
              </Grid>
              <SkeletonLoader
                skeletonHeight={100}
                isLoading={isLoading || isLoadingCategory}
              >
                <Grid item xs={12} sm={12}>
                  <Controller
                    name="reason"
                    control={control}
                    render={({ field }) => (
                      <Select
                        handleChange={(value) => field.onChange(value)}
                        data={reasons}
                        value={field.value as string}
                        label="Reason for reimbursement"
                        id="participant-reason-reimbursement"
                        isLoading={isLoadingCategory}
                        showClearOption={false}
                        error={!!errors.reason}
                        errorMessage={errors.reason?.message}
                        required={true}
                      />
                    )}
                  />
                </Grid>
              </SkeletonLoader>
              <Grid item xs={12} sm={12}>
                <Controller
                  name="amount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="amount"
                      label={"Amount"}
                      variant="outlined"
                      value={field.value}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val === "" ? "" : Number(val));
                      }}
                      error={!!errors.amount}
                      helperText={errors.amount ? errors.amount.message : null}
                      fullWidth
                      type="number"
                      required={true}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Typography
                  variant="titleSmall"
                  fontWeight={700}
                  gutterBottom
                  color={theme.palette.menu.dark}
                >
                  Additional information:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12}>
                <Controller
                  name="comments"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="amount"
                      label={"Comments"}
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.comments}
                      helperText={
                        errors.comments ? errors.comments.message : null
                      }
                      fullWidth
                      multiline={true}
                      rows={4}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        }
        actions={
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Button
              variantType="cancel"
              onClick={onClose}
              disabled={false}
              label={"Cancel"}
            />
            <Button
              variantType="save"
              onClick={handleReimbursement}
              disabled={addManualReimbursement.isPending}
              isLoading={addManualReimbursement.isPending}
              label={"REIMBURSE"}
            />
          </Box>
        }
      />

      {isConfirmationOpen && (
        <GenericAlertModal
          onClick={() => {
            handleSubmit(onSubmit)();
          }}
          onCancel={() => setIsConfirmationOpen(false)}
          title={"Reimburse confirmation"}
          description={"Are you sure you want to provide this reimbursement?"}
          submitText={t("confirm")}
          cancelText={t("cancel")}
          theme={theme}
        />
      )}
      <PermissionAlertModal />
    </>
  );
};

const addSchema = (t: TFunction) => {
  return yup.lazy(() => {
    const schemaFields: yup.ObjectShape = {
      reason: yup.string().required(t("validations.required")),
      amount: yup
        .number()
        .transform((value, originalValue) =>
          originalValue === "" || originalValue == null ? undefined : value
        )
        .typeError(t("validations.required"))
        .required(t("validations.required"))
        .min(0.1, t("validations.min_value", { minValue: 0.1 }))
        .test(
          "max-2-decimals",
          t("validations.max_decimals", { count: 2 }),
          (value) =>
            value == null ||
            (Number.isFinite(value) && Math.round(value * 100) === value * 100)
        ),
      comments: yup
        .string()
        .max(2000, t("validations.max_length", { count: 2000 })),
    };

    return yup.object().shape(schemaFields);
  });
};

type ManualReimbursementFormData = InferType<ReturnType<typeof addSchema>>;

export default ManualReimbursementModal;
